// Analytics: Besucher, Kanäle (inkl. KI-Suche), Conversion, SEO.
// Eine Route /analytics für beide Rollen:
//  - Kunde: eigene Daten, Projekt-Dropdown.
//  - Admin: Aggregat über alle Kunden + Drill-down pro Kunde.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/hooks/useAuth';
import { getCustomers, getProjects } from '@/lib/api';
import { getAnalyticsStats } from '@/lib/analytics-api';
import DateRangeFilter, { computeRange, type TimeRange } from '@/components/analytics/DateRangeFilter';
import StatCards from '@/components/analytics/StatCards';
import VisitorsChart from '@/components/analytics/VisitorsChart';
import ChannelBreakdown from '@/components/analytics/ChannelBreakdown';
import TopPagesTable from '@/components/analytics/TopPagesTable';
import CampaignsTable from '@/components/analytics/CampaignsTable';
import CustomerBreakdown from '@/components/analytics/CustomerBreakdown';
import SeoPanel from '@/components/analytics/SeoPanel';
import BlogPanel from '@/components/analytics/BlogPanel';
import PublivoConnector from '@/components/analytics/PublivoConnector';
import SeoRecommendations, { CampaignBuilderPanel, MonthlyReportPanel } from '@/components/analytics/SeoRecommendations';
import LandingpagesPanel from '@/components/analytics/LandingpagesPanel';
import ConversionKette from '@/components/analytics/ConversionKette';
import MassnahmeErfassen from '@/components/analytics/MassnahmeErfassen';
import StrategiePanel from '@/components/analytics/StrategiePanel';

type Tab = 'overview' | 'seo' | 'blog' | 'strategie' | 'ads' | 'bericht' | 'empfehlungen';
export type AnalyticsTab = Tab;

/** Eingebettet im Kunden-Arbeitsbereich (/kunden/:id): Kunde und Tab stehen
 *  fest, Kopfzeile, Kundenwahl und Tab-Leiste entfallen — nur Projekt- und
 *  Zeitraumwahl bleiben. Die URL-Parameter der umgebenden Seite (tab) bleiben
 *  unangetastet. */
export interface AnalyticsEmbedded { customerId: string; tab: Tab }

export default function AnalyticsPage({ embedded }: { embedded?: AnalyticsEmbedded } = {}) {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  // Auswahl lebt in der URL: Reload, Lesezeichen und Zurück-Button landen
  // wieder im selben Kontext (Kunde/Landingpage/Tab/Zeitraum), statt alles
  // neu zusammenklicken zu müssen.
  const [searchParams, setSearchParams] = useSearchParams();
  const validRange = (v: string | null): TimeRange | null =>
    v && (['7d', '30d', '90d', '12m', 'custom'] as string[]).includes(v) ? (v as TimeRange) : null;
  const validTab = (v: string | null): Tab | null =>
    v && (['overview', 'seo', 'blog', 'strategie', 'ads', 'bericht', 'empfehlungen'] as string[]).includes(v) ? (v as Tab) : null;

  const [range, setRange] = useState<TimeRange>(() => validRange(searchParams.get('range')) || '30d');
  const [customFrom, setCustomFrom] = useState(() => searchParams.get('von') || '');
  const [customTo, setCustomTo] = useState(() => searchParams.get('bis') || '');
  const [tab, setTab] = useState<Tab>(() => embedded?.tab || validTab(searchParams.get('tab')) || 'overview');
  useEffect(() => { if (embedded) setTab(embedded.tab); }, [embedded?.tab]);
  // Google-Ads-Tab: für welche beworbene Seite gerade eine Kampagne gebaut wird.
  const [seiteImFokus, setSeiteImFokus] = useState<string | null>(null);

  // Admin-Drill-down: welcher Kunde ist ausgewählt (null = Aggregat über alle)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(() => embedded?.customerId || searchParams.get('kunde'));
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => searchParams.get('projekt'));

  // Zustand → URL spiegeln. `replace`, damit jede Filter-Änderung nicht einen
  // eigenen History-Eintrag erzeugt (sonst müsste man 10× zurück drücken).
  useEffect(() => {
    // Eingebettet gehören `tab`/`kunde` der umgebenden Seite — nur die eigenen
    // Parameter (projekt, range, von, bis) anfassen, alles andere stehen lassen.
    const next = embedded ? new URLSearchParams(searchParams) : new URLSearchParams();
    if (embedded) for (const k of ['projekt', 'range', 'von', 'bis']) next.delete(k);
    if (!embedded && selectedCustomerId) next.set('kunde', selectedCustomerId);
    if (selectedProjectId) next.set('projekt', selectedProjectId);
    if (!embedded && tab !== 'overview') next.set('tab', tab);
    if (range !== '30d') next.set('range', range);
    if (range === 'custom') {
      if (customFrom) next.set('von', customFrom);
      if (customTo) next.set('bis', customTo);
    }
    // Nur schreiben, wenn sich die URL wirklich ändert: setSearchParams ändert
    // seine Identität bei jeder URL-Änderung, ein bedingungsloses Setzen könnte
    // sich sonst selbst wieder auslösen.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [selectedCustomerId, selectedProjectId, tab, range, customFrom, customTo, searchParams, setSearchParams]);

  const { from, to } = useMemo(() => computeRange(range, customFrom, customTo), [range, customFrom, customTo]);

  // Für Kunden: eigener Kunde; für Admin: der ausgewählte Kunde (oder alle)
  const scopeCustomerId = isAdmin ? selectedCustomerId : user?.customer_id || null;
  // X-Customer-Id nur senden, wenn Admin einen Kunden ausgewählt hat
  const headerCustomerId = isAdmin ? selectedCustomerId || undefined : undefined;

  // Projekte für das Dropdown (Kunde: eigene; Admin: die des ausgewählten Kunden)
  const { data: projects } = useQuery({
    queryKey: ['projects', scopeCustomerId],
    queryFn: () => getProjects(scopeCustomerId || undefined),
    enabled: isAdmin ? !!selectedCustomerId : true,
  });

  // Kundenname für die Drill-down-Überschrift
  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
    enabled: isAdmin,
  });
  const selectedCustomerName = customers?.find((c) => c.id === selectedCustomerId)?.name;
  const selectedProject = projects?.find((p) => p.id === selectedProjectId) || null;

  // Projektauswahl zurücksetzen, wenn der Kunde wechselt — aber NICHT beim
  // ersten Rendern, sonst würde die aus der URL wiederhergestellte Landingpage
  // sofort wieder verworfen.
  const kundeWechselGeprueft = useRef(false);
  useEffect(() => {
    if (!kundeWechselGeprueft.current) {
      kundeWechselGeprueft.current = true;
      return;
    }
    setSelectedProjectId(null);
  }, [selectedCustomerId]);

  // Hat der Kunde genau EINE Landing Page, direkt vorauswählen — dann
  // funktionieren SEO-/Blog-Tab sofort, ohne dass jemand das Dropdown anfasst.
  useEffect(() => {
    if (!selectedProjectId && projects?.length === 1) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  // Eingebettet im Kunden-Arbeitsbereich: Google-Ads- und SEO-Tab hängen an
  // EINEM Projekt. Statt „bitte Projekt auswählen" wird beim ersten Laden das
  // passende vorgewählt (Ads: das mit Ads-Konto, sonst kein Recruiting;
  // SEO/Blog/Strategie: die Website). Nur einmal — wer danach bewusst „Alle
  // Projekte" wählt, bleibt dabei.
  const vorauswahlErledigt = useRef(false);
  useEffect(() => {
    if (!embedded || vorauswahlErledigt.current || !projects?.length) return;
    vorauswahlErledigt.current = true;
    if (selectedProjectId) return;
    let wahl: typeof projects[number] | undefined;
    if (embedded.tab === 'ads') {
      wahl = projects.find((p) => p.ads_account_id) || projects.find((p) => p.project_type !== 'bewerbungen');
    } else if (embedded.tab === 'seo' || embedded.tab === 'blog' || embedded.tab === 'strategie') {
      wahl = projects.find((p) => p.project_type === 'website') || projects.find((p) => p.project_type === 'landingpage');
    }
    if (wahl) setSelectedProjectId(wahl.id);
  }, [embedded, projects, selectedProjectId]);

  const isAdminAggregate = isAdmin && !selectedCustomerId;
  // Reicht der Zeitraum bis heute? Ein abgeschlossener Monat ändert sich nicht
  // mehr — dort wäre Nachladen reine Last.
  const reichtBisHeute = to >= new Date().toISOString().slice(0, 10);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['analytics-stats', scopeCustomerId, selectedProjectId, from, to],
    queryFn: () => getAnalyticsStats({ from, to, projectId: selectedProjectId || undefined }, headerCustomerId),
    staleTime: 60_000,
    // Beim Wechsel von Zeitraum oder Projekt die alten Zahlen stehen lassen,
    // statt die Seite leer zu räumen.
    placeholderData: keepPreviousData,
    /*
     * Von selbst aktualisieren (Nico, 13.09.2026). Nur die Übersicht lebt
     * untertägig: SEO, Blog und Google Ads kommen aus den Nachtläufen um 03:00
     * und 03:30 — dort brächte Nachladen nichts außer Last.
     *
     * Ein Aufruf von /api/analytics/stats macht 13 bis 17 Datenbankabfragen,
     * im Admin-Aggregat über die Ereignisse ALLER Kunden. Deshalb zwei Minuten
     * je Kunde und fünf im Aggregat — und nicht zehn Sekunden wie auf der
     * Live-Seite, die nur ein schlankes 30-Minuten-Fenster liest.
     */
    refetchInterval: tab === 'overview' && reichtBisHeute ? (isAdminAggregate ? 300_000 : 120_000) : false,
    // Ein vergessener Tab im Hintergrund soll nicht stundenlang weiterfragen.
    refetchIntervalInBackground: false,
    // Wichtiger als jedes Intervall: frisch in dem Moment, in dem man hinsieht.
    refetchOnWindowFocus: true,
  });

  return (
    <div className="space-y-6">
      {/* Header — entfällt eingebettet im Kunden-Arbeitsbereich */}
      {!embedded && (
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {isAdmin && selectedCustomerId && (
              <button
                onClick={() => setSelectedCustomerId(null)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                title="Zurück zur Gesamtübersicht"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">
              Analytics
              {isAdmin && selectedCustomerName && (
                <span className="ml-2 text-lg font-normal text-gray-500">· {selectedCustomerName}</span>
              )}
            </h1>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {isAdminAggregate
              ? 'Besucher, Kanäle und Konversion über alle Kunden.'
              : 'Besucher, Traffic-Kanäle, Konversionsrate und SEO – automatisch aktualisiert.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && customers && (
            <select
              value={selectedCustomerId || ''}
              onChange={(e) => setSelectedCustomerId(e.target.value || null)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 max-w-[220px]"
              title="Kunde auswählen (für Blog-Verbindung & Detailansicht)"
            >
              <option value="">Alle Kunden (Aggregat)</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <DateRangeFilter
            range={range}
            onRangeChange={setRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition"
            title="Aktualisieren"
          >
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>
      )}

      {/* Sekundär-Leiste: Projekt-Dropdown + Tabs (nicht im Admin-Aggregat) */}
      {!isAdminAggregate && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {!embedded && (
          <div className="inline-flex flex-wrap rounded-lg border border-gray-200 bg-white p-0.5">
            {([
              { key: 'overview', label: 'Übersicht' },
              { key: 'seo', label: 'SEO / Google' },
              { key: 'blog', label: 'Blog (PUBLIVO)' },
              // Nur bei einem Website-Projekt: Die Strategie arbeitet an einer
              // ganzen Website mit Unterseiten. Bei einer einzelnen Ads-
              // Landingpage oder Recruiting gibt es nichts zu planen.
              ...(isAdmin && selectedProject?.project_type === 'website'
                ? [{ key: 'strategie', label: 'Strategie' }]
                : []),
              // Recruiting läuft über Meta, nicht über Google Ads — der Tab
              // hätte dort nichts zu zeigen.
              ...(isAdmin && selectedProject?.project_type !== 'bewerbungen'
                ? [{ key: 'ads', label: 'Google Ads' }]
                : []),
              ...(isAdmin ? [{ key: 'bericht', label: 'Monatsbericht' }] : []),
              ...(isAdmin ? [{ key: 'empfehlungen', label: '✨ Empfehlungen' }] : []),
            ] as Array<{ key: Tab; label: string }>).map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
                  tab === t.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          )}

          <div className="flex items-center gap-2 ml-auto">
          <select
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(e.target.value || null)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500"
          >
            {/* Wählt das PROJEKT, nicht eine beworbene Seite — die stehen im
                Google-Ads-Tab unter „Beworbene Landingpages". Das alte Label
                „Alle Landing Pages" hat genau diese zwei Ebenen verwechselt. */}
            <option value="">Alle Projekte</option>
            {(projects || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {embedded && (
            <>
              <DateRangeFilter range={range} onRangeChange={setRange} customFrom={customFrom} customTo={customTo} onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo} />
              <button onClick={() => refetch()} disabled={isFetching} className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition" title="Aktualisieren">
                {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </button>
            </>
          )}
          </div>
        </div>
      )}

      {/* Ein fehlgeschlagener Hintergrundlauf darf die Seite nicht leerräumen —
          sonst verliert man im Ads-Reiter getippte Formulare. Stattdessen ein
          Streifen über den weiter gültigen Zahlen. */}
      {error && data && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          Die Zahlen konnten gerade nicht aktualisiert werden — angezeigt wird der letzte Stand.
        </div>
      )}

      {/* Inhalt */}
      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : error && !data ? (
        <div className="bg-white rounded-lg border border-red-200 p-6 text-sm text-red-600">
          Analytics konnten nicht geladen werden: {(error as any).message}
        </div>
      ) : !data ? null : !isAdminAggregate && tab === 'seo' ? (
        <SeoPanel projectId={selectedProjectId} from={from} to={to} customerId={headerCustomerId} />
      ) : !isAdminAggregate && tab === 'blog' ? (
        !selectedProject ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
            Bitte oben eine einzelne Landing Page auswählen, um den PUBLIVO-Blog zu sehen.
          </div>
        ) : (
          <div className="space-y-4">
            {isAdmin && (
              <PublivoConnector
                projectId={selectedProject.id}
                projectName={selectedProject.name}
                connectedOrgId={selectedProject.publivo_org_id || null}
                projectGsc={selectedProject.gsc_property || null}
              />
            )}
            <BlogPanel projectId={selectedProject.id} from={from} to={to} customerId={headerCustomerId} />
          </div>
        )
      ) : !isAdminAggregate && tab === 'strategie' && isAdmin ? (
        !selectedProject ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
            Bitte oben ein einzelnes Projekt auswählen — die Strategie hängt an einer Website.
          </div>
        ) : (
          <StrategiePanel projectId={selectedProject.id} />
        )
      ) : !isAdminAggregate && tab === 'ads' && isAdmin ? (
        !selectedProject ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
            Bitte oben eine einzelne Landing Page auswählen — Kampagnen und Angles hängen am Projekt.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Ablauf von oben nach unten: Seite festlegen → Kampagne bauen →
                was schon läuft. Die Landingpage ist der Anker, nicht das Projekt.
                Die Strategie davor sitzt bewusst in einem eigenen Tab. */}
            {/* Zuerst der Gesundheitsstatus der Messkette: Ohne ankommende
                Conversions ist jede Optimierung darunter Blindflug. */}
            <ConversionKette projectId={selectedProject.id} />
            <LandingpagesPanel
              projectId={selectedProject.id}
              seiteImFokus={seiteImFokus}
              onKampagne={(url) => setSeiteImFokus(url)}
            />
            <CampaignBuilderPanel
              projectId={selectedProject.id}
              seiteImFokus={seiteImFokus}
              onFokusAufheben={() => setSeiteImFokus(null)}
            />
            {/* Direkt unter den Kampagnen: Wer hier gerade von Hand etwas
                umgestellt hat, trägt es im selben Arbeitsgang ein — sonst
                fehlt es später im Monatsbericht. */}
            <MassnahmeErfassen projectId={selectedProject.id} customerId={scopeCustomerId} />
            {((data.campaigns?.length || 0) > 0 || (data.excluded_campaigns?.length || 0) > 0) && (
              <CampaignsTable
                campaigns={data.campaigns || []}
                excluded={data.excluded_campaigns}
                projectId={selectedProjectId}
                isAdmin={isAdmin}
                onChanged={() => refetch()}
              />
            )}
          </div>
        )
      ) : tab === 'bericht' && isAdmin ? (
        !scopeCustomerId ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
            Bitte oben einen Kunden auswählen — der Monatsbericht fasst alle Pakete eines Kunden zusammen.
          </div>
        ) : (
          <MonthlyReportPanel customerId={scopeCustomerId} customerName={selectedCustomerName || 'Kunde'} />
        )
      ) : !isAdminAggregate && tab === 'empfehlungen' && isAdmin ? (
        !selectedProject ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
            Bitte oben eine einzelne Landing Page auswählen, um KI-Handlungssätze zu erzeugen.
          </div>
        ) : (
          <SeoRecommendations
                projectId={selectedProject.id}
                projectName={selectedProject.name}
                from={from}
                to={to}
              />
        )
      ) : (
        <>
          <StatCards
            visitors={data.totals.visitors}
            pageviews={data.totals.pageviews}
            leads={data.totals.leads}
            conversionRate={data.totals.conversion_rate}
            contactClicks={data.totals.contact_clicks}
          />

          <VisitorsChart data={data.timeseries} />

          {/* Admin-Aggregat: Kunden-Vergleich; sonst Kanäle + Seiten */}
          {isAdminAggregate ? (
            <>
              <ChannelBreakdown channels={data.channels} aiSources={data.ai_sources} from={from} to={to} projectId={selectedProjectId} />
              <CustomerBreakdown customers={data.by_customer || []} onSelect={setSelectedCustomerId} />
            </>
          ) : (
            <>
              <ChannelBreakdown channels={data.channels} aiSources={data.ai_sources} from={from} to={to} projectId={selectedProjectId} />
              {((data.campaigns?.length || 0) > 0 || (data.excluded_campaigns?.length || 0) > 0) && (
                <CampaignsTable
                  campaigns={data.campaigns || []}
                  excluded={data.excluded_campaigns}
                  projectId={selectedProjectId}
                  isAdmin={isAdmin}
                  onChanged={() => refetch()}
                />
              )}
              <div className="grid lg:grid-cols-2 gap-4">
                <TopPagesTable pages={data.pages} />
                {/* Projekt-Aufschlüsselung, wenn kein einzelnes Projekt gewählt */}
                {!selectedProjectId && data.projects.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h2 className="text-sm font-semibold text-gray-900">Landing Pages im Vergleich</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                            <th className="px-4 py-2 font-medium">Landing Page</th>
                            <th className="px-4 py-2 font-medium text-right">Besucher</th>
                            <th
                              className="px-4 py-2 font-medium text-right"
                              title={'Alle Kontaktaufnahmen: Formular-Anfragen plus Klicks auf Telefonnummer oder E-Mail.'}
                            >
                              Kontaktanfragen
                            </th>
                            <th className="px-4 py-2 font-medium text-right">Konversion</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {data.projects.map((p) => (
                            <tr
                              key={p.project_id}
                              onClick={() => setSelectedProjectId(p.project_id)}
                              className="hover:bg-primary-50 cursor-pointer"
                            >
                              <td className="px-4 py-2 font-medium text-gray-900">{p.name}</td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                {new Intl.NumberFormat('de-DE').format(p.visitors)}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                {p.kontakte ?? p.leads}
                                {(p.contact_clicks ?? 0) > 0 && (
                                  <span className="block text-[11px] font-normal text-gray-400">
                                    {p.leads} Formular · {p.contact_clicks} Tel./Mail
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                                {(p.conversion_rate * 100).toFixed(1)} %
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
